import type { ConsolaOptions, ConsolaReporter } from "consola";

export const LOG_LEVELS = {
  // Control levels
  SILENT: -999,
  VERBOSE: 999,

  // Error levels
  FATAL: 0,
  ERROR: 0,

  // Standard levels
  WARN: 1,
  LOG: 2,

  // Info levels
  INFO: 3,
  SUCCESS: 3,
  READY: 3,
  START: 3,

  // Debug levels
  DEBUG: 4,
  TRACE: 5,
} as const;

const defaultConsoleReporter: ConsolaReporter = {
  log: (logObj, ctx) => {
    // Extract the primary properties
    const { args = [], date, type, tag } = logObj;

    // The message is the first element in the args array
    const message =
      args.length > 0 && typeof args[0] === "string" ? args[0] : "";

    // The remaining args (skip the first if it was the message)
    const remainingArgs = message === args[0] ? args.slice(1) : args;

    // Format timestamp
    const timestamp =
      date instanceof Date ? date.toISOString() : new Date().toISOString();

    // Add tag if present
    const tagStr = tag ? `[${tag}] ` : "";

    // Map type to console method
    const method =
      type === "error" || type === "fatal"
        ? "error"
        : type === "warn"
          ? "warn"
          : type === "info" || type === "success"
            ? "info"
            : type === "debug" || type === "trace"
              ? "debug"
              : "log";

    // Log to console with formatting
    console[method](
      `[${timestamp}] [${type.toUpperCase()}] ${tagStr}${message}`,
      ...remainingArgs
    );
  },
};

export const DEFAULT_OPTIONS: Partial<ConsolaOptions> = {
  level: 3,
  // Change this line to include a default console reporter
  reporters: [defaultConsoleReporter],
  formatOptions: {
    date: true,
    colors: true,
    compact: false,
  },
};

export const ENV = {
  development: "development",
  production: "production",
  test: "test",
} as const;

// Export type helpers
export type LogLevel = keyof typeof LOG_LEVELS;
export type Environment = keyof typeof ENV;
